import {
  QN_ARTICLE_COUNT_PER_PAGE,
  QN_MYFEED_MAX_VIDEOS,
  QN_MYFEED_VIDEO_SPACING,
  QN_MYFEED_VIDEO_WINDOW_SIZE,
} from "@config/QuickNewsConstants";
import logger from "@src/logger/Logger";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import type { LatestVideoResponse } from "@typings/Brightcove.d";
import { MoreArticlesRequestData } from "@typings/MoreArticlesApi";
import {
  AdsCardProps,
  FeedItem,
  FinishCardProps,
  QNCardType,
  QuizCardProps,
  VideoItem,
} from "@typings/QuickNews";
import { useCallback, useMemo, useState } from "react";

import { getMoreFeeds, getMoreGallery, getMoreVideos } from "./QnLoadMore";

// --------------------------- Constants -------------------------------
export const END_CARD: FinishCardProps[] = [
  {
    id: `feed-end-success`,
    title: "Quick News Feed ended",
    name: "Quick News Feed ended",
    endCard: "Feeds Ended",
  },
];

const QUIZ_ITEM: QuizCardProps = {
  id: "tamil-quiz",
  title: "முரசு சொல்லாட்டம்",
  quizzes: "Random 10 Quizzes",
};

// --------------------------- Ad Helpers ------------------------------
const generateAdItems = (count: number): AdsCardProps[] =>
  Array.from({ length: count }, (_, i) => ({
    id: `imu2-card-${i + 1}`,
    adIndex: i + 1,
    title: "IMU2",
    name: "IMU2",
    adType: "IMU2",
  }));

export const insertAds = (items: FeedItem[]): FeedItem[] => {
  const withAds: FeedItem[] = [];
  const adItems = generateAdItems(Math.ceil(items.length / 4));

  items.forEach((item, index) => {
    withAds.push(item);
    if ((index + 1) % 3 === 0 && adItems.length > 0) {
      withAds.push(adItems.shift() as AdsCardProps);
    }
  });

  return withAds;
};

// --------------------------- Myfeed Video Helper ------------------------------
const insertVideosInMyFeed = (
  items: FeedItem[],
  videos: VideoItem[],
  startVideoIndex: number,
): {
  items: FeedItem[];
  nextVideoIndex: number;
} => {
  if (startVideoIndex >= videos.length) {
    return { items, nextVideoIndex: startVideoIndex };
  }

  const updatedItems = [...items];

  const windowStart =
    Math.floor((updatedItems.length - 1) / QN_MYFEED_VIDEO_WINDOW_SIZE) *
    QN_MYFEED_VIDEO_WINDOW_SIZE;

  const windowEnd = windowStart + QN_MYFEED_VIDEO_WINDOW_SIZE;
  const currentWindow = [...updatedItems.slice(windowStart, windowEnd)];

  const buildResponse = (nextVideoIndex: number) => {
    const mergedItems = [
      ...updatedItems.slice(0, windowStart),
      ...currentWindow,
      ...updatedItems.slice(windowEnd),
    ];

    mergedItems.forEach((item, index) => {
      if ("myFeedVideoIndex" in item) {
        item.myFeedVideoIndex = index;
      }
    });

    return {
      items: mergedItems,
      nextVideoIndex,
    };
  };

  const insertVideo = (index: number) => {
    currentWindow.splice(index, 0, {
      ...videos[startVideoIndex],
      myFeedVideoIndex: windowStart + index,
    });

    return buildResponse(startVideoIndex + 1);
  };

  const videoCount = currentWindow.filter(
    (item) => "myFeedVideoIndex" in item,
  ).length;

  // Maximum videos reached for this window
  if (videoCount >= QN_MYFEED_MAX_VIDEOS) {
    return buildResponse(startVideoIndex);
  }

  // First video
  if (videoCount === 0) {
    const articleCount = currentWindow.filter(
      (item) => !("adType" in item),
    ).length;

    if (articleCount < QN_MYFEED_VIDEO_SPACING) {
      return buildResponse(startVideoIndex);
    }

    return insertVideo(Math.min(3, currentWindow.length));
  }

  // Next video
  const lastVideoIndex = currentWindow.findLastIndex(
    (item) => "myFeedVideoIndex" in item,
  );

  if (lastVideoIndex < 0) {
    return buildResponse(startVideoIndex);
  }

  const nextIndex = lastVideoIndex + QN_MYFEED_VIDEO_SPACING;

  const adsCount = currentWindow
    .slice(lastVideoIndex + 1, nextIndex + 1)
    .filter((item) => "adType" in item).length;

  const hasQuiz = currentWindow.some((item) => "quizzes" in item);

  const insertIndex = nextIndex + adsCount + (hasQuiz ? 1 : 2);

  return windowStart + insertIndex <= updatedItems.length
    ? insertVideo(insertIndex)
    : buildResponse(startVideoIndex);
};

// ------------------------- Infinite Content Hook ---------------------
interface UseInfiniteContentReturn {
  content: FeedItem[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  isInitialized: boolean;
  loadMoreOlder: () => void;
}

const randomInt = (add_num: number): number =>
  Math.floor(Math.random() * add_num);

export const useInfiniteContent = ({
  Stories,
  videos,
  photoCards,
  initialActiveTab,
  initialId,
  enableVideos = false,
}: {
  Stories: ProcessedArticleData[];
  videos?: VideoItem[];
  photoCards: ProcessedArticleData[];
  initialActiveTab: QNCardType;
  initialId?: string;
  enableVideos?: boolean;
}): UseInfiniteContentReturn => {
  const initialContent = useMemo(() => {
    try {
      const merged: FeedItem[] = [...Stories];
      if (enableVideos && videos && videos.length) {
        const videoIndex = 2;
        merged.splice(videoIndex, 0, {
          ...videos[0],
          myFeedVideoIndex: videoIndex,
        });
      }
      if (photoCards.length) {
        const galleryInsertIndex = 4 + randomInt(2); // 5th or 6th position
        merged.splice(galleryInsertIndex, 0, ...photoCards);
      }

      return insertAds(merged);
    } catch {
      logger.error("Error computing initial content");
      return [];
    }
  }, [Stories, enableVideos, photoCards, videos]);

  const [articles, setArticles] = useState<ProcessedArticleData[]>(Stories);
  const [galleries, setGalleries] =
    useState<ProcessedArticleData[]>(photoCards);
  const [content, setContent] = useState<FeedItem[]>(initialContent);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreCount, setLoadMoreCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [quizAdded, setQuizAdded] = useState(false);
  const [isInitialized] = useState(true);
  const [loadOlderArticles, setLoadOlderArticles] = useState(false);

  const [nextVideoIndex, setNextVideoIndex] = useState(1);
  const [myFeedVideos, setMyFeedVideos] = useState<VideoItem[]>(videos ?? []);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    setError(null);

    try {
      const newLoadMoreCount = loadMoreCount + 1;
      setLoadMoreCount(newLoadMoreCount);

      const lastArticle = articles.at(-1);
      const requestData: MoreArticlesRequestData = { sort: lastArticle?.sort };

      const newArticles = await getMoreFeeds(requestData, loadOlderArticles);
      setArticles((prev) => [...prev, ...newArticles]);

      let newGalleries: ProcessedArticleData[] = [];
      if (newLoadMoreCount % 2 === 0) {
        const lastGallery = galleries.at(-1);
        const requestGalleryData: MoreArticlesRequestData = {
          sort: lastGallery?.sort,
        };
        newGalleries = await getMoreGallery(requestGalleryData);
        setGalleries((prev) => [...prev, ...newGalleries]);
      }

      setHasMore(newArticles.length === QN_ARTICLE_COUNT_PER_PAGE);

      let combinedItems: FeedItem[] = [...newArticles];
      if (newGalleries.length > 0) {
        const randomIndex = randomInt(newArticles.length + 1);
        combinedItems.splice(randomIndex, 0, ...newGalleries);
      }

      // Exclude initial active item
      const excludedId =
        initialActiveTab === "MyFeed" && initialId ? initialId : undefined;
      if (excludedId)
        combinedItems = combinedItems.filter((item) => item.id !== excludedId);

      let availableVideos = myFeedVideos;
      if (
        initialActiveTab === "MyFeed" &&
        nextVideoIndex >= myFeedVideos.length &&
        myFeedVideos.length < QN_MYFEED_VIDEO_WINDOW_SIZE
      ) {
        const nextVideos = await getMoreVideos(myFeedVideos.length);

        availableVideos = [
          ...myFeedVideos,
          ...nextVideos.slice(
            0,
            QN_MYFEED_VIDEO_WINDOW_SIZE - myFeedVideos.length,
          ),
        ];

        setMyFeedVideos(availableVideos);
      }

      setContent((prevContent) => {
        let updatedContent = prevContent.filter((item) => !("adType" in item));

        updatedContent.push(...combinedItems);

        // Insert quiz
        if (newLoadMoreCount === 1 && !quizAdded) {
          const randomPosition = 6 + randomInt(6);

          updatedContent.splice(randomPosition, 0, QUIZ_ITEM);

          setQuizAdded(true);
        }

        // Insert Ads
        updatedContent = insertAds(updatedContent);

        // Insert Videos
        if (enableVideos && availableVideos.length > 0) {
          const result = insertVideosInMyFeed(
            updatedContent,
            availableVideos,
            nextVideoIndex,
          );

          updatedContent = result.items;
          setNextVideoIndex(result.nextVideoIndex);
        }
        return updatedContent;
      });
      if (newArticles.length < QN_ARTICLE_COUNT_PER_PAGE)
        setContent((prev) => [...prev, ...END_CARD]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load more content",
      );
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    hasMore,
    loadMoreCount,
    articles,
    loadOlderArticles,
    initialActiveTab,
    initialId,
    myFeedVideos,
    nextVideoIndex,
    galleries,
    quizAdded,
    enableVideos,
  ]);

  const loadMoreOlder = useCallback(() => {
    setLoadOlderArticles(true);
    setHasMore(true);
  }, []);

  return {
    content,
    loading,
    error,
    hasMore,
    loadMore,
    isInitialized,
    loadMoreOlder,
  };
};

// ------------------------- Infinite Videos Hook ---------------------
interface UseInfiniteVideosReturn {
  videos: VideoItem[];
  loading: boolean;
  error: string | null;
  hasMoreVideos: boolean;
  loadMoreVideos: () => Promise<void>;
}

export const useInfiniteVideos = ({
  bcVideos,
  initialId,
  initialActiveTab,
}: {
  bcVideos?: LatestVideoResponse[];
  initialId?: string;
  initialActiveTab: QNCardType;
}): UseInfiniteVideosReturn => {
  const [videos, setVideos] = useState<VideoItem[]>(bcVideos || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreCount, setLoadMoreCount] = useState(0);
  const [hasMoreVideos, setHasMoreVideos] = useState(true);

  const loadMoreVideos = useCallback(async () => {
    if (loading || !hasMoreVideos) return;

    setLoading(true);
    setError(null);

    try {
      const newLoadMoreCount = loadMoreCount + 1;
      setLoadMoreCount(newLoadMoreCount);

      const offset = videos.length;
      const newVideos = await getMoreVideos(offset);

      const excludedId = initialActiveTab === "Videos" ? initialId : undefined;
      const filteredVideos = excludedId
        ? newVideos.filter((v) => v.id !== excludedId)
        : newVideos;

      setVideos((prev) => [...prev, ...filteredVideos]);
      if (!newVideos.length) setVideos((prev) => [...prev, ...END_CARD]);
      setHasMoreVideos(newVideos.length > 0);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load more videos",
      );
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    hasMoreVideos,
    loadMoreCount,
    initialId,
    initialActiveTab,
    videos,
  ]);

  return { videos, loading, error, hasMoreVideos, loadMoreVideos };
};
